import type { EditorCommand, ExtractInjectionAPI } from '@atlaskit/editor-common/types';
import type { BlockControlsPlugin } from '@atlaskit/editor-plugin-block-controls';
import { NodeSelection, TextSelection } from '@atlaskit/editor-prosemirror/state';
import { CellSelection } from '@atlaskit/editor-tables';
import { expValEqualsNoExposure } from '@atlaskit/tmp-editor-statsig/exp-val-equals-no-exposure';

import { malleableUiPluginKey } from '../pm-plugins/plugin-key';
import type { RemixThinkingPluginMeta } from '../pm-plugins/remix-thinking/remix-thinking-plugin';
import { remixThinkingPluginKey } from '../pm-plugins/remix-thinking/remix-thinking-plugin';
import type { RemixModalMetadata } from '../types';

export const makeStartRemixThinking =
	(api: ExtractInjectionAPI<BlockControlsPlugin> | undefined) =>
	({
		contentType,
		remixModalMetadata,
	}: { contentType?: string; remixModalMetadata?: RemixModalMetadata } = {}): EditorCommand =>
	({ tr }) => {
		const selection = tr.selection;
		const isNodeSelection = selection instanceof NodeSelection;
		const isCellSelection = selection instanceof CellSelection;

		const thinkingMeta: RemixThinkingPluginMeta = {
			contentType,
			isThinking: true,
			thinkingSelectionFrom: selection.from,
			thinkingSelectionTo: isNodeSelection
				? selection.from + selection.node.nodeSize
				: selection.to,
			isNodeSelection: isNodeSelection,
		};

		if (isCellSelection) {
			thinkingMeta.cellSelectionRanges = selection.ranges.map((range) => ({
				from: range.$from.pos,
				to: range.$to.pos,
			}));
			thinkingMeta.anchorCellPos = selection.$anchorCell.pos;
			thinkingMeta.headCellPos = selection.$headCell.pos;
		}

		// Clear block selection decorations before applying the thinking state.
		api?.selection?.commands?.clearBlockSelection()({ tr });

		tr.setMeta(remixThinkingPluginKey, thinkingMeta);

		/**
		 * Added this check so that when a userPrompt is pre-supplied in metadata (e.g. from the AI Image Generation flow),
		 * we can pass in necessary metadata to the malleable-ui plugin.
		 * like the userPrompt
		 */
		if (remixModalMetadata && remixModalMetadata.userPrompt && expValEqualsNoExposure('cc_page_experiences_editor_image_generation', 'isEnabled', true)) {
			tr.setMeta(malleableUiPluginKey, { isRemixModalOpen: false, remixModalMetadata });
		} else {
			tr.setMeta(malleableUiPluginKey, { isRemixModalOpen: false });
		}

		// Collapse the cursor to the end of the selection so the user can
		// continue editing while the remix is processing.
		const endPos = isNodeSelection ? selection.from + selection.node.nodeSize : selection.to;
		tr.setSelection(TextSelection.create(tr.doc, endPos));

		return tr;
	};
